import React, { useCallback, useEffect } from "react";
import { useState } from "react";

import DataFrame from "dataframe-js";
import { Tab, Tabs } from "react-bootstrap";

import Widget from "../../../components/Elements/Widget";
import { AssetLink, SoilProfile } from "../../../types/types";
import StackedPlot from "../../../components/Elements/StackedPlot";
import {
  colorPlot,
  conductivityPlot,
  saturationAcidityPlot,
  structurePlot,
  texturePlot,
  waterPlot,
  healthPlot,
} from "../utils/charts";
import { useGetProfileCsv } from "../../../api/useGetProfileCsv";
import AssetModal from "../../../components/Elements/AssetModal";
import ProfileVideoButton from "./ProfileVideoButton";
import { useStore } from '../../../providers/AppProvider';
import { useUnitConversion, formatUnitAbbreviation} from '../../../utils/unitConversion';
import { dcRound } from '../../../utils/misc';
import _ from 'lodash';

type Props = {
  soilProfile: SoilProfile | null;
};

const BelowWidget = ({ soilProfile }: Props) => {
  console.log("soilProfile", soilProfile);
  const [data, setData] = useState<any>(null);
  const { data: csvData, isLoading } = useGetProfileCsv(soilProfile);
  const [selectedAssetLink, setSelectedAssetLink] = useState<AssetLink | null>(null);
  const [showVideo, setShowVideo] = useState<boolean>(false);
  const [maxDepthCmArray, setMaxDepthCmArray] = useState<Array<Array<number>>>([[0,0]]);
  const [maxVideoTime, setMaxVideoTime] = useState<number>(0);
  const [minVideoTime, setMinVideoTime] = useState<number>(0);
  const [depthIndicatorPosition, setDepthIndicatorPosition] = useState<number>(0);
  const [depthIndicatorCm, setDepthIndicatorCm] = useState<number>(0);

  const { convertToPreferredUnitValue, convertToPreferredUnitString, unitsConvert } = useUnitConversion();
  const { localePreferences } = useStore();

  // preferred area unit abbr (for label and submission)
  const preferredAreaMid = _.get(localePreferences, 'measureUnits.area.mid');
  const preferredAreaAbbr: string =
      typeof preferredAreaMid === 'string' ? preferredAreaMid : preferredAreaMid?.abbr || 'ac';

  // [LEN] preferred length unit abbr (for spacing label/input + submission)
  const preferredLenSmall = _.get(localePreferences, 'measureUnits.length.small');
  const preferredLenSmallAbbr: string =
      typeof preferredLenSmall === 'string' ? preferredLenSmall : preferredLenSmall?.abbr || 'ft';

  useEffect(() => {
    console.log("isLoading", isLoading);
    console.log("csvData", csvData);

    // Guard: prevent setState after unmount or tab switch
    let alive = true;

    if (!isLoading && csvData) {
      const file = new File([csvData], "temp.csv", { type: "text/plain" });
      console.log("082825 file", file);

      DataFrame.fromCSV(file).then((df0: any) => {
        if (!alive) return;

        let df = df0;
        const columns = df.listColumns();
        for (let col = 0; col < columns.length; col++) {
          df = df.rename(columns[col], columns[col].toLocaleLowerCase());
        }

        df = df.filter((row: any) => {
          if (row.get("video time (s)") === "") {
            // keep empty rows out
          } else {
            return row.get("video time (s)");
          }
        });

        if (!alive) return;

        console.log("082825 df", df);
        let availableColumns = df.listColumns();

        // Normalize poros if it was percentage
        if (availableColumns.includes("pores")) {
          const porosPointArray = df.toArray("poros");
          const allValuesBetween0And1Poros = porosPointArray.every(
            (v: number) => v >= 0 && v <= 1
          );
          if (!allValuesBetween0And1Poros) {
            df = df.map((row: any) => row.set("poros", row.get("poros") / 100));
          }
        }

        if (!alive) return;
        setData(df);

        const maxCm = Math.ceil(Number(df.stat.max("depth (cm)")) / 10) * 10;
        const cmIntervals: Array<[number, number]> = [];
        let i = 0;
        while (i <= maxCm) {
          if (i % 10 === 0) {
            cmIntervals.push([i, (i / maxCm) * 100]);
          }
          i++;
        }

        if (!alive) return;
        setMinVideoTime(Number(df.stat.min("video time (s)")));
        setMaxVideoTime(Number(df.stat.max("video time (s)")));
        setMaxDepthCmArray(cmIntervals);
        setDepthIndicatorPosition(0);
        setDepthIndicatorCm(0);

        const timeDepthArray = df.select("video time (s)", "depth (cm)").toArray();
        timeDepthArray.unshift([
          "video time (s) - 4x less than video time",
          "depth (cm)",
        ]);
      });
    }

    // Cleanup: flip the flag so pending async work does nothing
    return () => {
      alive = false;
    };
  }, [csvData, isLoading]);


  useEffect(() => {
    setData(null);
  }, [soilProfile]);

  const onPlotClick = useCallback(
    (depth: number) => {
      depth = Math.round(depth);

      // plot allows selection outside of the range, clamp to the range of asset
      const depths = soilProfile?.assetLinks.map((l: AssetLink) => l.value);
      const maxDepth = Math.max(...depths!);
      depth = depth > maxDepth ? maxDepth : depth;
      depth = depth < 1 ? 1 : depth;

      const asset = soilProfile?.assetLinks.find(
        (link: AssetLink) => link.asset_type == 'depth_snapshot' && link.value === depth
      );
      if (asset) {
        setSelectedAssetLink(asset);
      }
    },
    [soilProfile, data]
  );

  const handleTimeUpdate = (e:any) => {
    console.log("Video currentTime / 4", e.target.currentTime / 4);

    const maxTime = Number(data.stat.max("video time (s)"))
    const maxDepth = Number(data.stat.max("depth (cm)"))

    let beforeDepthTime = 0;
    let beforeDepthCm = 0;
    const beforeDepthArray = data.filter((row:any ) =>  row.get("video time (s)") <= (e.target.currentTime / 4 ) );

    if (beforeDepthArray.toArray().length > 0){
      let indexA = beforeDepthArray.toArray("video time (s)").length - 1;
      beforeDepthTime = Number(beforeDepthArray.toArray("video time (s)")[indexA]);
      beforeDepthCm = Number(beforeDepthArray.toArray("depth (cm)")[indexA]);
    }
    
    let afterDepthTime = maxTime;
    let afterDepthCm = maxDepth;
    const afterDepthArray = data.filter((row:any ) =>  row.get("video time (s)") >= (e.target.currentTime / 4 ) );

    if (afterDepthArray.toArray().length > 0){
      let indexB = 0;
      afterDepthTime = Number(afterDepthArray.toArray("video time (s)")[indexB]);
      afterDepthCm = Number(afterDepthArray.toArray("depth (cm)")[indexB]);
    }

    if (maxDepthCmArray.length > 0){
      let addedInterpolation = 0;
      if (afterDepthTime > beforeDepthTime){
        let tare = (( e.target.currentTime / 4 ) - beforeDepthTime) / (afterDepthTime - beforeDepthTime);
        addedInterpolation = tare * (afterDepthCm - beforeDepthCm);
      }

      setDepthIndicatorPosition(( (beforeDepthCm + addedInterpolation) / maxDepthCmArray[maxDepthCmArray.length - 1][0] ) * 100 );
      setDepthIndicatorCm((beforeDepthCm + addedInterpolation));

    }
  }

  // --- NEW: local unit helpers for AssetModal displays (minimal change) ---
  const useInches = preferredLenSmallAbbr !== 'cm';
  const cmToIn = (1 / 2.5)
  const formatDepth = (cm: number) => useInches ? `${(Number(cm) * cmToIn).toFixed(1)} in` : `${Math.round(Number(cm))} cm`;
  const unitLabel = useInches ? 'in' : 'cm';

  return (
    <>
      <Widget
        title={`Soil Push #${soilProfile?.name}`}
        renderActions={() => <></>}
      >
        {!isLoading && data && soilProfile 
        ? (
          <div className="w-100 p-1">
            {soilProfile.maskedVideoUrl && <ProfileVideoButton onClick={() => setShowVideo(true)} />}

            <Tabs
              unmountOnExit={true}
              defaultActiveKey="profile"
              transition={false}
              id="tabs"
              className="p-2 mb-3"
            >
              <Tab eventKey="profile" title="Profile" tabClassName="text-sm p-2">
                <div>
                  <StackedPlot
                    config={colorPlot(soilProfile, data, preferredLenSmallAbbr)}
                    onClick={onPlotClick}
                  />
                </div>
              </Tab>
              <Tab eventKey="texture" title="Texture" tabClassName="text-sm p-2">
                <StackedPlot
                  config={texturePlot(soilProfile, data, preferredLenSmallAbbr)}
                  onClick={onPlotClick}
                />
              </Tab>
              <Tab eventKey="structure" title="Structure" tabClassName="text-sm p-2">
                <StackedPlot
                  config={structurePlot(data, preferredLenSmallAbbr)}
                  onClick={onPlotClick}
                />
              </Tab>
              <Tab eventKey="water" title="Water" tabClassName="text-sm p-2">
                <StackedPlot
                  config={waterPlot(soilProfile, data, preferredLenSmallAbbr)}
                  onClick={onPlotClick}
                />
              </Tab>
              {/**/}<Tab eventKey="saturation-acidity" title="pH" tabClassName="text-sm p-2">
                <StackedPlot
                  config={saturationAcidityPlot(soilProfile, data, preferredLenSmallAbbr)}
                  onClick={onPlotClick}
                />
              </Tab>
              <Tab eventKey="conductivity" title="Salts" tabClassName="text-sm p-2">
                <StackedPlot
                  config={conductivityPlot(data, preferredLenSmallAbbr)}
                  onClick={onPlotClick}
                />
              </Tab>
              <Tab eventKey="carbon" title="Carbon" tabClassName="text-sm p-2">
                <StackedPlot
                  config={healthPlot(soilProfile, data, preferredLenSmallAbbr)}
                  onClick={onPlotClick}
                />
              </Tab>
            </Tabs>
          </div>
        ) 
        : <div className="w-100 p-1"></div>
      }
      </Widget>

      {soilProfile && selectedAssetLink && (
        <AssetModal
          show={true}
          title={`Push #${soilProfile.name}: Depth ${useInches ? `${(selectedAssetLink.value * cmToIn).toFixed(1)} in` : `${selectedAssetLink.value} cm`}`}
          onClose={() => setSelectedAssetLink(null)}
        >
          <img src={selectedAssetLink.s3_key} style={{ width: "100%" }} />
        </AssetModal>
      )}

      {soilProfile && soilProfile.maskedVideoUrl && showVideo && (
        <AssetModal
          show={showVideo}
          title={`Push #${soilProfile.name}`}
          onClose={() => setShowVideo(false)}
        >
          <div style={{width:"100%", height: "100%", display: "inline-flex"}}>
            <div style={{width:"95px", paddingBottom: "40px", fontSize: "12px", minWidth: "95px"}} >
            <div style={{fontWeight:"bold", position:"relative", top:"-10px", borderBottom:"0.5px solid #fff", marginRight:"20px"}} >{`depth (${unitLabel})`}</div>
              { maxDepthCmArray.map((item, index) => (
                <div 
                  key={`${index}-depth`}
                  className={"div-interval-label"}
                  style={{top:`${item[1]}%`}}
                >
                  {useInches ? (item[0] * cmToIn).toFixed(1) : item[0]}
                  <div 
                    className={"div-interval-label-tic"}
                  ></div>
                </div>
              ))
              }
              <div 
                key={`current-depth`}
                className={"div-interval-label"}
                style={{top:`${depthIndicatorPosition}%`, left:"45px", color:"#6d757d"}}
              >
                  {useInches ? `${(depthIndicatorCm * cmToIn).toFixed(1)} in` : `${depthIndicatorCm.toFixed(0)} cm`}
                <div 
                  className={"div-interval-label-tic"}
                  style={{backgroundColor:"#6d757d", width:"20px", left:"-22px"}}
                ></div>
              </div>
            </div>
            
            <div style={{width:"calc(100% - 95px)"}} >
              <div
                  style={{
                    // Leave the scalebar scaled to 100 microns. (Per WW, 1 pixel = ~1.2 microns)
                    // Video resolution is 1920 × 1080 
                    // Goal: draw a 100 µm scale bar
                    // Given calibration: 1 pixel ≈ 1.2 µm → 1 µm ≈ 0.8333 px
                    // Pixels for 100 µm = 100 µm × (1 px / 1.2 µm) = 83.333… px
                    // As a fraction of a 1920 px-wide video: 83.333 / 1920 = 0.04340278 = 4.340278%
                    // Therefore, width ≈ 4.34% of the rendered video width (equiv. to 100% / 23.04 ≈ 100% / 23)
                    width: "4.3403%",
                    height: "2px",
                    backgroundColor: "white",
                    position: "relative",
                    top: "50px",
                    left: "30px",
                  }} 
              >
                <div
                    style={{
                      width: "400%",
                      height: "20px",
                      position: "relative",
                      top: "-15px",
                      left: "-150%",
                      fontSize:"8px",
                      textAlign:"center",
                    }} 
                >
                  {"100 microns" /*useInches ? "1 in (25,400 μm)" : "100 microns"*/}
                </div>
              </div>
              <video
                onTimeUpdate={handleTimeUpdate}
                src={`${soilProfile.maskedVideoUrl}#t=${ minVideoTime * 4 }`}
                controls
                className="w-100 mt-2"
                id="soilProbeVideo"
              >
                Your browser does not support this video
              </video>
            </div>
          </div>
        </AssetModal>
      )}
    </>
  );
};

export default BelowWidget;
